// ---------------------------------------------------------------------------
// The round-by-round table.
//
// Three decisions carry it, and they were all paid for once already:
//
//   - Fifteen rounds is a legal game, so the columns keep their width and the
//     table scrolls inside its own container rather than squeezing numbers until
//     they stop being readable.
//   - The headers are abbreviated, `L1` through `L10`, because `Letters 10`
//     spelled out put a legal game past the width of the page it sits in. The
//     long name is still each header's accessible name.
//   - **A teams game is scored by team, so the table is grouped by team.** It
//     used to be a flat list of seated players sorted by score, which in a teams
//     game printed the team's total once per member and never named the team at
//     all. Member rows stay, because best-of scoring means who carried which
//     round is real information, and their own totals are muted so the team's is
//     the number.
//
// It lived in `EndGameScreen.tsx` and read `ClientGameState` directly, which is
// why moving it needed more than a cut and paste: the game record page has a
// finished game rather than a room, and it must not have to invent a live state
// to show one. So the table takes the two things it actually draws — a list of
// rounds reduced to per-player cells, and the blocks in standing order — and
// each caller builds those from what it has. `blocksFromClientState` below is
// the room's builder; the record screen has its own.
// ---------------------------------------------------------------------------

import type { ClientGameState, ClientRound, ClientSubmission, PublicPlayer, RoundType, Team } from '@countdown/shared';

import { PlayerChip } from '../primitives/PlayerChip.js';
import { RoundSequenceSummary } from '../primitives/RoundSequenceSummary.js';
import { listNames, roundLabels } from '../../lib/format.js';
import { membersOfTeam, scoreFor } from '../../lib/playerLookup.js';

/** Per-round column width when the table has to scroll. */
const ROUND_COLUMN_PX = 48;
const RANK_COLUMN_PX = 46;
const NAME_COLUMN_PX = 200;
const TOTAL_COLUMN_PX = 96;

/** What one player did in one round, reduced to the two facts a cell draws. */
export interface BreakdownCell {
  score: number;
  /** Struck out by the judge or by the host. Drawn in the alert colour. */
  rejected: boolean;
}

/** A round, as far as this table is concerned. */
export interface BreakdownRound {
  index: number;
  type: RoundType;
  /** Keyed by player id. A player who did not answer may simply be absent. */
  cells: Record<string, BreakdownCell>;
}

/**
 * One line of the standing: a team and its members, or one unteamed player.
 *
 * `perRound` is what the bucket scored, which for a team is its best member's
 * answer rather than their sum. `placement` matches the engine's `placements`
 * exactly — one plus however many finished strictly ahead, so a tie shares a
 * place and the next one skips.
 */
export interface BreakdownBlock {
  key: string;
  team: Team | null;
  members: PublicPlayer[];
  perRound: number[];
  total: number;
  placement: number;
}

function rejected(submission: ClientSubmission): boolean {
  if (submission.hostOverride === 'reject') return true;
  if (submission.hostOverride === 'allow') return false;
  return submission.valid === false;
}

/** A live round reduced to what the table needs. */
export function roundsFromClientState(rounds: ClientRound[]): BreakdownRound[] {
  return rounds.map((round) => {
    const cells: Record<string, BreakdownCell> = {};
    for (const [playerId, submission] of Object.entries(round.submissions)) {
      cells[playerId] = { score: submission.score ?? 0, rejected: rejected(submission) };
    }
    return { index: round.index, type: round.type, cells };
  });
}

/** The room's standing, grouped by team when the game had them. */
export function blocksFromClientState(
  state: ClientGameState,
  rows: PublicPlayer[],
): BreakdownBlock[] {
  const rounds = state.rounds;
  const teamed = state.teams.length > 0;
  const blocks: BreakdownBlock[] = [];

  const scoreOf = (round: ClientRound, playerId: string) =>
    round.submissions[playerId]?.score ?? 0;

  if (teamed) {
    for (const team of state.teams) {
      const members = membersOfTeam(state, team.id).filter((player) => player.role === 'player');
      if (members.length === 0) continue;
      blocks.push({
        key: team.id,
        team,
        members,
        perRound: rounds.map((round) =>
          members.reduce((best, player) => Math.max(best, scoreOf(round, player.id)), 0),
        ),
        total: state.teamScores[team.id] ?? 0,
        placement: 1,
      });
    }
  }

  for (const player of rows) {
    if (teamed && player.teamId !== null) continue;
    blocks.push({
      key: player.id,
      team: null,
      members: [player],
      perRound: rounds.map((round) => scoreOf(round, player.id)),
      total: scoreFor(state, player),
      placement: 1,
    });
  }

  return rankBlocks(blocks);
}

/** Highest total first. A tie keeps whatever order the caller built them in. */
export function sortBlocks(blocks: BreakdownBlock[]): BreakdownBlock[] {
  return [...blocks].sort((a, b) => b.total - a.total);
}

/**
 * Sort, and derive `placement` from the totals.
 *
 * One plus however many finished strictly ahead, so a tie shares a place and the
 * next one skips — the same rule as the engine's `placements`.
 *
 * Only for buckets whose placement is not already known. A finished game's SEAT
 * placement is stored, and the stored one is the engine's own answer: it is what
 * `wins` counts on the leaderboard, so a page that derives its own can disagree
 * with the profile beside it. Teams have no stored placement, which is why this
 * still exists.
 */
export function rankBlocks(blocks: BreakdownBlock[]): BreakdownBlock[] {
  const sorted = sortBlocks(blocks);
  for (const block of sorted) {
    block.placement = sorted.filter((other) => other.total > block.total).length + 1;
  }
  return sorted;
}

function ScoreCell({ score, tone }: { score: number; tone: string }) {
  return <td className={`tabular py-3 text-center font-mono font-semibold ${tone}`}>{score}</td>;
}

export function Breakdown({
  rounds,
  blocks,
}: {
  rounds: BreakdownRound[];
  blocks: BreakdownBlock[];
}) {
  const labels = roundLabels(rounds.map((round) => round.type));
  const bestPerRound = rounds.map((_, index) =>
    blocks.reduce((highest, block) => Math.max(highest, block.perRound[index]), 0),
  );
  const teamed = blocks.some((block) => block.team !== null);

  const minWidth =
    RANK_COLUMN_PX + NAME_COLUMN_PX + TOTAL_COLUMN_PX + rounds.length * ROUND_COLUMN_PX;

  return (
    <section className="flex flex-col gap-3 rounded-frame border border-line bg-bg px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="label-mono">round by round</span>
        <div className="ml-auto">
          <RoundSequenceSummary sequence={rounds.map((round) => round.type)} />
        </div>
      </div>

      {/* Fifteen rounds is a legal game, so the columns keep their width and the
          table scrolls sideways instead of the page breaking. */}
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-testid="breakdown-scroll">
        <table className="w-full table-fixed border-collapse" style={{ minWidth }}>
          <thead>
            <tr className="border-b border-line">
              <th
                className="label-mono py-2 text-left"
                scope="col"
                style={{ width: RANK_COLUMN_PX }}
              >
                pos
              </th>
              <th
                className="label-mono py-2 text-left"
                scope="col"
                style={{ width: NAME_COLUMN_PX }}
              >
                {teamed ? 'team' : 'player'}
              </th>
              {labels.map((label, index) => (
                <th
                  key={`${label.short}-${index}`}
                  className="label-mono py-2 text-center"
                  scope="col"
                  style={{ minWidth: ROUND_COLUMN_PX }}
                >
                  {/* The short form is what fits; the long one is what the
                      header is called to anything reading the table out. */}
                  <span aria-hidden="true">{label.short}</span>
                  <span className="sr-only">{label.long}</span>
                </th>
              ))}
              <th
                className="label-mono py-2 text-right"
                scope="col"
                style={{ width: TOTAL_COLUMN_PX }}
              >
                total
              </th>
            </tr>
          </thead>

          {blocks.map((block) => (
            <tbody key={block.key}>
              <tr className="border-b border-line/60">
                <td className="tabular py-3 text-left font-mono text-ink-dim">{block.placement}</td>
                <th className="py-3 text-left font-normal" scope="row">
                  {block.team === null ? (
                    <PlayerChip player={block.members[0]} size="sm" />
                  ) : (
                    <span className="flex flex-col gap-0.5">
                      <span className="font-semibold">{block.team.name}</span>
                      <span className="text-xs text-ink-dim">
                        {listNames(block.members.map((player) => player.displayName))}
                      </span>
                    </span>
                  )}
                </th>
                {rounds.map((round, index) => {
                  const score = block.perRound[index];
                  const isBest = bestPerRound[index] > 0 && score === bestPerRound[index];
                  const solo = block.team === null ? block.members[0] : null;
                  const cell = solo === null ? undefined : round.cells[solo.id];
                  const wasRejected = cell !== undefined && cell.rejected;
                  return (
                    <ScoreCell
                      key={round.index}
                      score={score}
                      tone={
                        wasRejected
                          ? 'text-alert'
                          : isBest
                            ? 'text-blue-200'
                            : score === 0
                              ? 'text-ink-dim'
                              : 'text-ink'
                      }
                    />
                  );
                })}
                <td className="tabular py-3 text-right text-xl font-extrabold">{block.total}</td>
              </tr>

              {block.team === null
                ? null
                : block.members.map((player) => (
                    <tr key={player.id} className="border-b border-line/60">
                      <td />
                      <th className="py-2 pl-4 text-left font-normal" scope="row">
                        <PlayerChip player={player} size="sm" />
                      </th>
                      {rounds.map((round, index) => {
                        const cell = round.cells[player.id];
                        const score = cell?.score ?? 0;
                        const wasRejected = cell !== undefined && cell.rejected;
                        // The one the team took, which is the whole of best-of.
                        const taken = score > 0 && score === block.perRound[index];
                        return (
                          <ScoreCell
                            key={round.index}
                            score={score}
                            tone={
                              wasRejected ? 'text-alert' : taken ? 'text-ink' : 'text-ink-dim'
                            }
                          />
                        );
                      })}
                      <td className="tabular py-2 text-right font-mono text-sm text-ink-dim">
                        {rounds.reduce((sum, round) => sum + (round.cells[player.id]?.score ?? 0), 0)}
                      </td>
                    </tr>
                  ))}
            </tbody>
          ))}
        </table>
      </div>

      {teamed ? (
        <p className="text-sm text-ink-muted">
          A team takes its best answer each round, so the members do not add up to the team.
        </p>
      ) : null}
    </section>
  );
}
